import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';

interface ReceiptViewerModalProps {
  open: boolean;
  onClose: () => void;
  expenseId: string;
  receiptId: string;
  expenseTitle?: string;
  expenseAmount?: number;
  expenseDate?: string;
}

interface ReceiptInfo {
  receipt_id: string;
  file_extension: string;
  file_size: number;
  status: string;
  download_url: string;
}

const ReceiptViewerModal: React.FC<ReceiptViewerModalProps> = ({
  open,
  onClose,
  expenseId,
  receiptId,
  expenseTitle,
  expenseAmount,
  expenseDate
}) => {
  const [receiptInfo, setReceiptInfo] = useState<ReceiptInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'loading' | 'viewer' | 'error'>('loading');

  useEffect(() => {
    if (open && receiptId) {
      fetchReceiptInfo();
    }
  }, [open, receiptId]);

  const fetchReceiptInfo = async () => {
    setLoading(true);
    setError(null);
    setViewMode('loading');

    try {
      const response = await api.get(`/api/revolut/expenses/${expenseId}/receipts-info`);
      
      if (response.data.success) {
        const receipts = response.data.receipts;
        const receipt = receipts.find((r: any) => r.receipt_id === receiptId);
        
        if (receipt && receipt.exists) {
          setReceiptInfo(receipt);
          setViewMode('viewer');
        } else {
          // Receipt doesn't exist locally - provide helpful error message
          const receiptStatus = receipt?.status || 'not_downloaded';
          if (receiptStatus === 'not_downloaded') {
            setError('Receipt file not found. The receipt may need to be downloaded first. Please use the "Fetch All Receipts" button to download receipts from Revolut.');
          } else {
            setError('Receipt not found or not available. The receipt may not be available from Revolut API.');
          }
          setViewMode('error');
        }
      } else {
        setError('Failed to fetch receipt information');
        setViewMode('error');
      }
    } catch (err: any) {
      console.error('Error fetching receipt info:', err);
      const errorMessage = err.response?.data?.error || 'Failed to load receipt';
      // Provide more helpful error message
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        setError('Receipt not found. The receipt may need to be downloaded first. Please use the "Fetch All Receipts" button to download receipts from Revolut.');
      } else {
        setError(errorMessage);
      }
      setViewMode('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!receiptInfo) return;
    
    const downloadUrl = `${api.defaults.baseURL}/api/revolut/receipts/${receiptId}?download=true`;
    window.open(downloadUrl, '_blank');
  };

  const handleOpenInNewTab = () => {
    if (!receiptInfo) return;
    
    const viewUrl = `${api.defaults.baseURL}/api/revolut/receipts/${receiptId}`;
    window.open(viewUrl, '_blank');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (extension: string) => {
    switch (extension.toLowerCase()) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
        return <ImageIcon color="primary" />;
      default:
        return <ReceiptIcon color="action" />;
    }
  };

  const renderViewer = () => {
    if (!receiptInfo) return null;

    const isPdf = receiptInfo.file_extension.toLowerCase() === 'pdf';
    const isImage = ['png', 'jpg', 'jpeg'].includes(receiptInfo.file_extension.toLowerCase());
    
    const receiptUrl = `${api.defaults.baseURL}/api/revolut/receipts/${receiptId}`;

    return (
      <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
        {/* Receipt Header */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            {getFileIcon(receiptInfo.file_extension)}
            <Typography variant="h6" component="div">
              Receipt {receiptInfo.receipt_id.slice(-8)}
            </Typography>
            <Chip 
              label={receiptInfo.file_extension.toUpperCase()} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              label={formatFileSize(receiptInfo.file_size)} 
              size="small" 
              color="secondary" 
              variant="outlined" 
            />
          </Stack>
          
          {expenseTitle && (
            <Typography variant="body2" color="text.secondary">
              <strong>Expense:</strong> {expenseTitle}
            </Typography>
          )}
          {expenseAmount && (
            <Typography variant="body2" color="text.secondary">
              <strong>Amount:</strong> €{expenseAmount.toFixed(2)}
            </Typography>
          )}
          {expenseDate && (
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {new Date(expenseDate).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Receipt Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {isPdf ? (
            <iframe
              src={receiptUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={`Receipt ${receiptInfo.receipt_id}`}
            />
          ) : isImage ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <img
                src={receiptUrl}
                alt={`Receipt ${receiptInfo.receipt_id}`}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain' 
                }}
                onError={() => setViewMode('error')}
              />
            </Box>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
              <Stack spacing={2} alignItems="center">
                {getFileIcon(receiptInfo.file_extension)}
                <Typography variant="h6" color="text.secondary">
                  Preview not available for {receiptInfo.file_extension.toUpperCase()} files
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Download File
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          Receipt Viewer
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {viewMode === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Stack spacing={2} alignItems="center">
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading receipt...
              </Typography>
            </Stack>
          </Box>
        )}

        {viewMode === 'error' && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || 'Failed to load receipt'}
            </Alert>
            <Button
              variant="outlined"
              onClick={fetchReceiptInfo}
              disabled={loading}
            >
              Retry
            </Button>
          </Box>
        )}

        {viewMode === 'viewer' && renderViewer()}
      </DialogContent>

      {viewMode === 'viewer' && receiptInfo && (
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
          >
            Open in New Tab
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ReceiptViewerModal;
